import React from 'react';

// ─── Brand colours ────────────────────────────────────────────────────────────
// dark-teal: #1e562d   muted-blue: #7d9483   dark-gray: #434344
// ─────────────────────────────────────────────────────────────────────────────

export const STEPS = [
  'Personal Info',
  'Emergency Contact',
  'Insurance',
  'Clinical Background',
  'Consents',
];

const INSURANCE_PROVIDERS = [
  'Cigna Healthcare',
  'CenCal',
  'Beacon Health Value Options',
  'Beacon Health Strategies (GOLD COAST)',
  'Beacon Health Strategies (LA Care)',
  'Private Pay',
  'Other',
];

export const initialForm = {
  // Step 1 — Personal Info (Bucket A)
  preferredName: '',
  phone: '',
  dateOfBirth: '',
  street: '',
  city: '',
  state: '',
  zip: '',
  employerOrSchool: '',
  paymentPreference: '',

  // Step 2 — Emergency Contact (Bucket A)
  emergencyContactName: '',
  emergencyContactPhone: '',

  // Step 3 — Insurance (Bucket A + Bucket B category)
  insuranceProvider: '',
  insurancePlanName: '',
  insuranceMemberId: '',
  insuranceGroupId: '',
  insurancePhone: '',
  deductible: '',
  copay: '',
  oopMax: '',
  insuranceType: '',        // Bucket B — category only

  // Step 4 — Clinical Background (Bucket B)
  maritalStatus: '',
  gender: '',
  employmentStatus: '',
  referralSource: '',
  reasonForReachingOut: '',
  medicationsCurrent: false,
  medicationsList: '',

  // Step 5 — Consents (Bucket A)
  consentInformedConsent: false,
  consentCancellationPolicy: false,
  consentTelehealth: false,
};

// ─── Shared helpers — defined OUTSIDE component to keep stable references ─────

const Label = ({ children, required }) => (
  <label className="block text-sm font-medium mb-1" style={{ color: '#434344' }}>
    {children}{required && <span className="text-red-500 ml-1">*</span>}
  </label>
);

const Field = ({ label, required, children }) => (
  <div className="mb-4">
    <Label required={required}>{label}</Label>
    {children}
  </div>
);

// Input and Select receive value+onChange explicitly so they can live outside
const Input = ({ name, type = 'text', placeholder, value, onChange, ...rest }) => (
  <input
    name={name} type={type} placeholder={placeholder}
    value={value} onChange={onChange}
    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2"
    style={{ '--tw-ring-color': '#1e562d' }}
    {...rest}
  />
);

const Select = ({ name, value, onChange, children }) => (
  <select
    name={name} value={value} onChange={onChange}
    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 bg-white"
  >
    {children}
  </select>
);

export const Progress = ({ step }) => (
  <div className="mb-8">
    <div className="flex justify-between mb-1">
      {STEPS.map((label, i) => (
        <span key={label}
          className="text-xs font-medium"
          style={{ color: i <= step ? '#1e562d' : '#7d9483' }}>
          {label}
        </span>
      ))}
    </div>
    <div className="w-full bg-gray-200 rounded-full h-2">
      <div
        className="h-2 rounded-full transition-all duration-300"
        style={{ width: `${((step + 1) / STEPS.length) * 100}%`, backgroundColor: '#1e562d' }}
      />
    </div>
  </div>
);

const ConsentBlock = ({ name, title, body, checked, onChange }) => (
  <div className="mb-6 border border-gray-200 rounded-lg p-4">
    <h4 className="font-semibold text-sm mb-2" style={{ color: '#1e562d' }}>{title}</h4>
    <p className="text-xs text-gray-500 mb-3 leading-relaxed">{body}</p>
    <label className="flex items-start gap-2 cursor-pointer">
      <input
        type="checkbox" name={name}
        checked={checked} onChange={onChange}
        className="mt-0.5 w-4 h-4 accent-teal-700 flex-shrink-0"
      />
      <span className="text-sm font-medium" style={{ color: '#434344' }}>
        I have read and agree to the above.
      </span>
    </label>
  </div>
);

// ─── Step panels — defined outside, receive form+handle as props ──────────────

export const StepPersonal = ({ form, handle, currentUser }) => (
  <>
    <p className="text-xs text-gray-400 mb-6">
      Your name and email are already on file. Fill in the remaining details below.
    </p>

    <div className="grid grid-cols-2 gap-4 mb-4">
      <div>
        <Label>First Name</Label>
        <div className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-500">
          {currentUser?.firstName || '—'}
        </div>
      </div>
      <div>
        <Label>Last Name</Label>
        <div className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-500">
          {currentUser?.lastName || '—'}
        </div>
      </div>
    </div>

    <Field label="Preferred Name" required>
      <Input name="preferredName" placeholder="What would you like to be called?"
        value={form.preferredName} onChange={handle} />
    </Field>

    <div className="grid grid-cols-2 gap-4">
      <Field label="Phone" required>
        <Input name="phone" type="tel" placeholder="(805) 555-0100"
          value={form.phone} onChange={handle} />
      </Field>
      <Field label="Date of Birth" required>
        <Input name="dateOfBirth" type="date"
          value={form.dateOfBirth} onChange={handle} />
      </Field>
    </div>

    <Field label="Street Address" required>
      <Input name="street" placeholder="123 Main St"
        value={form.street} onChange={handle} />
    </Field>

    <div className="grid grid-cols-3 gap-4">
      <Field label="City" required>
        <Input name="city" placeholder="Santa Barbara"
          value={form.city} onChange={handle} />
      </Field>
      <Field label="State" required>
        <Input name="state" placeholder="CA" maxLength={2}
          value={form.state} onChange={handle} />
      </Field>
      <Field label="Zip" required>
        <Input name="zip" placeholder="93101" maxLength={10}
          value={form.zip} onChange={handle} />
      </Field>
    </div>

    <Field label="Employer / School" required>
      <Input name="employerOrSchool" placeholder="Where do you work or study?"
        value={form.employerOrSchool} onChange={handle} />
    </Field>

    <Field label="Payment Preference" required>
      <Select name="paymentPreference" value={form.paymentPreference} onChange={handle}>
        <option value="">Select…</option>
        <option value="insurance">Insurance Benefits</option>
        <option value="private_pay">Private Pay</option>
      </Select>
    </Field>
  </>
);

export const StepEmergency = ({ form, handle }) => (
  <>
    <p className="text-xs text-gray-400 mb-6">
      This person will be contacted only in the case of an emergency.
    </p>
    <Field label="Emergency Contact Name" required>
      <Input name="emergencyContactName" placeholder="Full name"
        value={form.emergencyContactName} onChange={handle} />
    </Field>
    <Field label="Emergency Contact Phone" required>
      <Input name="emergencyContactPhone" type="tel" placeholder="(805) 555-0200"
        value={form.emergencyContactPhone} onChange={handle} />
    </Field>
  </>
);

export const StepInsurance = ({ form, handle, preview = false }) => (
  <>
    <Field label="Insurance Category" required>
      <Select name="insuranceType" value={form.insuranceType} onChange={handle}>
        <option value="">Select…</option>
        <option value="medicaid">Medicaid / MediCal</option>
        <option value="private">Private Insurance</option>
        <option value="self-pay">Self-Pay</option>
        <option value="other">Other</option>
      </Select>
    </Field>

    {(preview || form.insuranceType === 'private' || form.insuranceType === 'medicaid') && (
      <>
        <Field label="Insurance Provider" required>
          <Select name="insuranceProvider" value={form.insuranceProvider} onChange={handle}>
            <option value="">Select…</option>
            {INSURANCE_PROVIDERS.map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
          </Select>
        </Field>
        <Field label="Insurance Plan Name" required>
          <Input name="insurancePlanName" placeholder="Plan name"
            value={form.insurancePlanName} onChange={handle} />
        </Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Member ID / Policy #" required>
            <Input name="insuranceMemberId" placeholder="Member ID"
              value={form.insuranceMemberId} onChange={handle} />
          </Field>
          <Field label="Group Number" required>
            <Input name="insuranceGroupId" placeholder="Group #"
              value={form.insuranceGroupId} onChange={handle} />
          </Field>
        </div>
        <Field label="Insurance Phone" required>
          <Input name="insurancePhone" type="tel" placeholder="(800) 555-0000"
            value={form.insurancePhone} onChange={handle} />
        </Field>
        <div className="grid grid-cols-3 gap-4">
          <Field label="Deductible" required>
            <Input name="deductible" placeholder="$0"
              value={form.deductible} onChange={handle} />
          </Field>
          <Field label="Copay" required>
            <Input name="copay" placeholder="$0"
              value={form.copay} onChange={handle} />
          </Field>
          <Field label="Out-of-Pocket Max" required>
            <Input name="oopMax" placeholder="$0"
              value={form.oopMax} onChange={handle} />
          </Field>
        </div>
      </>
    )}

    <div className="mt-4 p-3 rounded-lg text-xs text-gray-500" style={{ backgroundColor: '#e8eff3' }}>
      <strong>Note:</strong> Payment card information is collected separately and securely through our payment processor. Card numbers are never stored in our system.
    </div>
  </>
);

export const StepClinical = ({ form, handle, preview = false }) => (
  <>
    <div className="grid grid-cols-2 gap-4">
      <Field label="Marital Status" required>
        <Select name="maritalStatus" value={form.maritalStatus} onChange={handle}>
          <option value="">Select…</option>
          {['Married', 'Single', 'Divorced', 'Separated', 'Widowed', 'Other'].map(o => (
            <option key={o} value={o}>{o}</option>
          ))}
        </Select>
      </Field>
      <Field label="Gender / Pronouns" required>
        <Select name="gender" value={form.gender} onChange={handle}>
          <option value="">Select…</option>
          {['He/Him', 'She/Her', 'They/Them', 'Other'].map(o => (
            <option key={o} value={o}>{o}</option>
          ))}
        </Select>
      </Field>
    </div>

    <div className="grid grid-cols-2 gap-4">
      <Field label="Employment Status" required>
        <Select name="employmentStatus" value={form.employmentStatus} onChange={handle}>
          <option value="">Select…</option>
          {['Full Time', 'Part Time', 'Unemployed', 'Self-Employed', 'Attending School', 'Other'].map(o => (
            <option key={o} value={o}>{o}</option>
          ))}
        </Select>
      </Field>
      <Field label="How did you hear about us?" required>
        <Select name="referralSource" value={form.referralSource} onChange={handle}>
          <option value="">Select…</option>
          {['Insurance Website', 'Psychology Today', 'Personal Referral', 'shanebruce.com', 'Other'].map(o => (
            <option key={o} value={o}>{o}</option>
          ))}
        </Select>
      </Field>
    </div>

    <Field label="What brings you to therapy?" required>
      <textarea
        name="reasonForReachingOut"
        value={form.reasonForReachingOut}
        onChange={handle}
        rows={4}
        placeholder="Please describe what's been going on and what you're hoping to work on…"
        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 resize-none"
      />
      <p className="text-xs text-gray-400 mt-1">
        Please do not include your name in this field.
      </p>
    </Field>

    <Field label="Are you currently taking any medications?">
      <label className="flex items-center gap-2 cursor-pointer mb-2">
        <input
          type="checkbox" name="medicationsCurrent"
          checked={form.medicationsCurrent} onChange={handle}
          className="w-4 h-4"
        />
        <span className="text-sm text-gray-700">Yes, I am currently taking medications</span>
      </label>
    </Field>

    {(preview || form.medicationsCurrent) && (
      <Field label="Please list your medications" required>
        <textarea
          name="medicationsList"
          value={form.medicationsList}
          onChange={handle}
          rows={3}
          placeholder="Medication name, dosage, frequency…"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 resize-none"
        />
      </Field>
    )}
  </>
);

export const StepConsents = ({ form, handle }) => (
  <>
    <p className="text-xs text-gray-400 mb-6">
      Please read and acknowledge each policy below before submitting.
    </p>

    <ConsentBlock
      name="consentInformedConsent"
      title="Informed Consent for Therapy"
      body="I understand that therapy involves discussing personal and sensitive matters. Shane Bruce Mental Health & Coaching provides services in marriage and family therapy. Sessions are 50 minutes and a minimum commitment of 6 weekly sessions is expected. Confidentiality will be maintained except as required by law (e.g., abuse reporting, danger to self or others). I have the right to terminate treatment at any time."
      checked={form.consentInformedConsent}
      onChange={handle}
    />

    <ConsentBlock
      name="consentCancellationPolicy"
      title="Cancellation & Late Cancellation Policy"
      body="I agree to provide at least 48 hours notice to cancel or reschedule an appointment. If I cancel with less than 48 hours notice or do not show up, I will be charged the full contracted session rate. This fee is not covered by insurance."
      checked={form.consentCancellationPolicy}
      onChange={handle}
    />

    <ConsentBlock
      name="consentTelehealth"
      title="Informed Consent for Telehealth Services"
      body="I understand that my therapy sessions may be conducted via telehealth (video or phone). I consent to the use of telehealth technology for my treatment. I understand that telehealth has the same confidentiality protections as in-person sessions. I acknowledge that technology failures may occasionally disrupt sessions and agree to a backup communication plan with my therapist."
      checked={form.consentTelehealth}
      onChange={handle}
    />
  </>
);

